import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share, Bell, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from '@/hooks/useAuth';

export const SafariNotificationGuide = () => {
  const { user } = useAuth();
  const storageKey = `safari-guide-collapsed-${user?.id || 'anonymous'}`;
  
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === null ? true : stored !== 'true'; // Default open, stored 'true' means collapsed
  });

  useEffect(() => {
    localStorage.setItem(storageKey, String(!isOpen));
  }, [isOpen, storageKey]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-primary/20">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                <CardTitle className="text-base sm:text-lg">Push notificaties instellen voor Safari/iOS</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            <CardDescription className="text-left">
              Volg deze stappen om push notificaties te ontvangen op je iPhone, iPad of Mac
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  1
                </div>
                <div>
                  <p className="font-medium">Voeg app toe aan startscherm</p>
                  <p className="text-sm text-muted-foreground">
                    Tik op het Share icoon <Share className="inline h-4 w-4" /> onderaan je scherm (iOS) of in de adresbalk (Mac)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  2
                </div>
                <div>
                  <p className="font-medium">Selecteer "Zet op beginscherm"</p>
                  <p className="text-sm text-muted-foreground">
                    Scroll naar beneden in het menu en kies deze optie
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  3
                </div>
                <div>
                  <p className="font-medium">Open de app vanaf je startscherm</p>
                  <p className="text-sm text-muted-foreground">
                    Zodra de app is toegevoegd, open hem vanaf je startscherm
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  4
                </div>
                <div>
                  <p className="font-medium">Sta notificaties toe</p>
                  <p className="text-sm text-muted-foreground">
                    Je krijgt een melding om push notificaties toe te staan - kies "Toestaan"
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-muted p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-1">Waarom deze stappen?</p>
                  <p className="text-muted-foreground">
                    Safari vereist dat progressive web apps (PWA's) worden geïnstalleerd voordat push notificaties kunnen worden ontvangen. Dit zorgt voor een betere app-ervaring met volledige toegang tot native functies.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
